import React, { useState, useMemo, useEffect, useRef } from "react";
import { Star } from "lucide-react";
import CustomSelect from "../components/ui/CustomSelect";
import { useSiteAnalytics } from "../hooks/useSiteAnalytics";
import { useMonthlyReport } from "../hooks/useMonthlyReport";
import { useInvoiceReport } from "../hooks/useInvoiceReport";
import { useReportSettings } from "../hooks/useReportSettings";
import { useSiteFootprintReport } from "../hooks/useSiteFootprintReport";
import { useClosedSiteFinancialsReport } from "../hooks/useClosedSiteFinancialsReport";
import { useAuth } from "../hooks/useAuth";
import { canMutateTenantData } from "../config/constants";
import ReportBudgetCard from "../components/report20/ReportBudgetCard";
import { useYtdReport } from "../hooks/useYtdReport";
import MonthYearSelector from "../components/analytics/MonthYearSelector";
import Report20YtdComboChart from "../components/report20/Report20YtdComboChart";
import Report20YtdBudgetTrendChart from "../components/report20/Report20YtdBudgetTrendChart";
import Report20MonthlyFinancialLedger from "../components/report20/Report20MonthlyFinancialLedger";
import Report20ProviderTable from "../components/report20/Report20ProviderTable";
import Report20SpendByGlCard from "../components/report20/Report20SpendByGlCard";
import Report20TopSitesTable from "../components/report20/Report20TopSitesTable";
import Report20CategoryBarChart from "../components/report20/Report20CategoryBarChart";
import Report20ProviderDonutChart from "../components/report20/Report20ProviderDonutChart";
import Report20FootprintBaselineCard from "../components/report20/Report20FootprintBaselineCard";
import Report20SiteFootprintChangesView from "../components/report20/Report20SiteFootprintChangesView";
import Report20ClosedSiteFinancialsView from "../components/report20/Report20ClosedSiteFinancialsView";
import Report20DataLoadingOverlay from "../components/report20/Report20DataLoadingOverlay";
import Report20MrcHistoryView from "../components/report20/Report20MrcHistoryView";
import Report20DisconnectsView from "../components/report20/Report20DisconnectsView";
import { SpinnerCustom } from "../components/ui/Spinner";
import { formatGlCodeLabel } from "../utils/glCodes";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatClosedThrough(lastClosedMonth, closedMonthsCount, year) {
  const monthNum = Number(lastClosedMonth != null ? lastClosedMonth : closedMonthsCount);
  if (!Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) return "—";
  const monthName = MONTH_NAMES[monthNum - 1];
  return year ? `${monthName} ${year}` : monthName;
}

// function getPriorMonthYear() {
//   const d = new Date();
//   d.setMonth(d.getMonth() - 1);
//   return { year: d.getFullYear(), month: d.getMonth() + 1 };
// }

function getPriorMonthYearUTC() {
  const d = new Date();
  d.setUTCDate(1); // prevent overflow issues
  d.setUTCMonth(d.getUTCMonth() - 1);

  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
  };
}

function parseVarianceSign(formattedVariance) {
  if (!formattedVariance || typeof formattedVariance !== "string") return "neutral";
  if (formattedVariance.includes("Over Budget")) return "over";
  if (formattedVariance.includes("Under Budget")) return "under";
  return "neutral";
}

function parseMoneyString(s) {
  if (s == null || s === "" || s === "—") return null;
  const n = parseFloat(String(s).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** Full USD (matches API / Report 2.0 card formatting). */
function formatFullUsd(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatUsd0(n) {
  if (n == null || !Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

const ytdControlClass =
  "h-[38px] w-full min-w-[8.5rem] rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2.5 text-[13px] text-[var(--ink,#1c1c1a)] focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]";

const YtdKpiCard = ({ label, value, hint, badge, valueClassName = "text-[var(--ink,#1c1c1a)]", tooltip }) => (
  <div className="group relative rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
    <div className="flex items-start justify-between gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
        {label}
      </p>
      {badge ? (
        <span className="inline-flex rounded-full bg-[var(--sunken,#eeece7)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
          {badge}
        </span>
      ) : null}
    </div>
    <p className={`mt-1 text-[22px] font-semibold tabular-nums leading-tight ${valueClassName}`}>{value}</p>
    {hint ? <p className="mt-1 text-[12px] leading-snug text-[var(--muted,#6b6a66)]">{hint}</p> : null}
    {tooltip ? (
      <div className="pointer-events-none absolute left-1/2 top-0 z-20 w-max max-w-[240px] -translate-x-1/2 -translate-y-full opacity-0 transition-opacity group-hover:opacity-100">
        <div className="relative rounded-md bg-[#1c1c1a] px-3 py-2 text-center text-[11px] text-white shadow-lg">
          {tooltip}
          <div className="absolute left-1/2 top-full -translate-x-1/2 border-[6px] border-transparent border-t-[#1c1c1a]" />
        </div>
      </div>
    ) : null}
  </div>
);

const Report20Page = ({
  tenantId,
  setHeaderOptionsContent,
  focusSection = null,
  ytdBudgetOnly = false,
  footprintOnly = false,
  closedSitesFinancialsOnly = false,
  spendOverviewOnly = false,
  mrcHistoryOnly = false,
  disconnectsOnly = false,
  wirelessUsageOnly = false,
  showReportBudgetCard = false,
  glCodes = [],
}) => {
  const { userRole } = useAuth();
  const canEditReportBudget = canMutateTenantData(userRole);

  const ytdBudgetSectionRef = useRef(null);
  const ytdExportRef = useRef(null);
  const topSitesSectionRef = useRef(null);
  const footprintSectionRef = useRef(null);
  const { year: defaultYear, month: defaultMonth } = getPriorMonthYearUTC();
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedGlCode, setSelectedGlCode] = useState("");

  // const now = new Date();
  // const currentYear = now.getFullYear();
  // const currentMonth = now.getMonth() + 1;
  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth() + 1;
  const reportYear = selectedYear ?? currentYear;
  const reportMonth = selectedMonth ?? currentMonth;
  const isReportCurrentMonth = reportYear === currentYear && reportMonth === currentMonth;

  useEffect(() => {
    if (!setHeaderOptionsContent) return;
    if (ytdBudgetOnly || spendOverviewOnly || mrcHistoryOnly || disconnectsOnly || footprintOnly || closedSitesFinancialsOnly || wirelessUsageOnly) {
      setHeaderOptionsContent(null);
      return () => setHeaderOptionsContent(null);
    }
    setHeaderOptionsContent(
      <div className="flex justify-between items-center w-full">
        <div className="telecom-title-wrap">
          <h1 className="client-logo-title">Telecom Expense Management System</h1>
          <p className="telecom-title-tagline">Report</p>
        </div>
      </div>
    );
    return () => setHeaderOptionsContent(null);
  }, [setHeaderOptionsContent, ytdBudgetOnly, spendOverviewOnly, mrcHistoryOnly, disconnectsOnly, footprintOnly, closedSitesFinancialsOnly, wirelessUsageOnly]);

  const isHistorical = selectedYear !== null && selectedMonth !== null;
  const {
    data: currentData,
    loading: siteAnalyticsLoading,
    error: siteAnalyticsError,
    refresh: refreshSiteAnalytics,
  } = useSiteAnalytics(tenantId);
  const { data: invoiceReportData, loading: invoiceReportLoading, error: invoiceReportError, refresh: refreshInvoiceReport } =
    useInvoiceReport(
      tenantId,
      closedSitesFinancialsOnly || spendOverviewOnly || mrcHistoryOnly || disconnectsOnly || wirelessUsageOnly ? null : reportYear,
      closedSitesFinancialsOnly || spendOverviewOnly || mrcHistoryOnly || disconnectsOnly || wirelessUsageOnly ? null : reportMonth
    );
  const {
    data: siteFootprintData,
    loading: siteFootprintLoading,
    error: siteFootprintError,
    refresh: refreshSiteFootprint,
  } = useSiteFootprintReport(tenantId, reportYear, reportMonth);
  const {
    data: closedSiteFinancialsData,
    loading: closedSiteFinancialsLoading,
    error: closedSiteFinancialsError,
    refresh: refreshClosedSiteFinancials,
  } = useClosedSiteFinancialsReport(
    tenantId,
    closedSitesFinancialsOnly ? reportYear : null,
    closedSitesFinancialsOnly ? reportMonth : null
  );
  // const { data: historicalData } = useMonthlyReport(tenantId, selectedYear, selectedMonth);
  const { data: historicalData } = {}
  const { annualBudgetByYear, footprintBaselineByYear, providerBudgetByYear, updateBudget, updateFootprintBaseline, refresh: refreshReportSettings } =
    useReportSettings();

  const annualBudget = useMemo(() => {
    const raw = annualBudgetByYear?.[String(reportYear)];
    const n = parseFloat(raw);
    // Provider budgets are often negative (expense convention); still valid for YTD.
    return Number.isFinite(n) ? n : null;
  }, [annualBudgetByYear, reportYear]);

  /** Provider Budget vs Spend only when that year's budget was tracked by provider (not whole). */
  const showProviderBudgetVsSpend = useMemo(() => {
    const entryType = providerBudgetByYear?.[String(reportYear)]?.entryType;
    return entryType !== "whole";
  }, [providerBudgetByYear, reportYear]);

  const yearOptions = useMemo(() => {
    const existingYears = Object.keys(annualBudgetByYear || {}).map(Number);
  
    const currentYear = new Date().getFullYear();
  
    const minYear = Math.min(...existingYears, currentYear - 5);
    const maxYear = Math.max(...existingYears, currentYear + 8);
  
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  }, [annualBudgetByYear]);

  useEffect(() => {
    if (mrcHistoryOnly) {
      setSelectedYear(currentYear);
    }
  }, [mrcHistoryOnly, currentYear]);

  const { data: ytdData, loading: ytdLoading, error: ytdError, refresh: refreshYtd } = useYtdReport(
    reportYear,
    reportMonth ? reportMonth : null,
    annualBudget,
    ytdBudgetOnly ? selectedGlCode : ""
  );

  const glCodeOptions = useMemo(() => {
    const list = Array.isArray(glCodes) ? glCodes : [];
    return [...list]
      .filter((g) => g && g.gl_code)
      .sort((a, b) =>
        String(a.gl_code).localeCompare(String(b.gl_code), undefined, { numeric: true }),
      );
  }, [glCodes]);

  const displayData = useMemo(() => {
    if (invoiceReportData) {
      return {
        totalSpent: invoiceReportData.totalSpent,
        totalSites: invoiceReportData.totalSites,
        totalCircuits: invoiceReportData.totalCircuits ?? invoiceReportData.dataCircuitsCount,
        providerSpending: invoiceReportData.providerSpending || [],
        topSites: invoiceReportData.topSites || [],
        allSites: invoiceReportData.allSites || [],
        snapshot_data: invoiceReportData.snapshot_data,
        dataCircuitsCount: invoiceReportData.dataCircuitsCount,
      };
    }
    if (isHistorical && historicalData) {
      return {
        ...historicalData,
        dataCircuitsCount: historicalData.totalCircuits,
      };
    }
    return currentData ? { ...currentData, dataCircuitsCount: currentData.totalCircuits } : null;
  }, [invoiceReportData, isHistorical, historicalData, currentData]);

  const dimStaleWhileLoading = "pointer-events-none opacity-45 select-none transition-opacity";
  const ytdStaleLoading = Boolean(ytdData) && ytdLoading && annualBudget != null;

  const ytdWait = annualBudget != null && ytdLoading;
  const loading = spendOverviewOnly
    ? siteAnalyticsLoading
    : mrcHistoryOnly || disconnectsOnly
    ? false
    : ytdBudgetOnly
    ? ytdWait
    : footprintOnly
    ? siteFootprintLoading
    : closedSitesFinancialsOnly
    ? closedSiteFinancialsLoading
    : invoiceReportLoading || ytdWait;
  const error = spendOverviewOnly
    ? siteAnalyticsError
    : mrcHistoryOnly || disconnectsOnly
    ? null
    : footprintOnly
    ? siteFootprintError
    : closedSitesFinancialsOnly
    ? closedSiteFinancialsError
    : invoiceReportError;

  useEffect(() => {
    if (footprintOnly || closedSitesFinancialsOnly || mrcHistoryOnly || disconnectsOnly) return;
    if (!focusSection) return;
    // Dedicated YTD Budget page is already that view. Re-scrolling whenever
    // ytdData refreshes jumps the user back to the top (e.g. away from
    // Provider Spend vs. Budget).
    if (ytdBudgetOnly) return;
    if (spendOverviewOnly && focusSection === "spendOverview" && !currentData) return;
    if (!spendOverviewOnly && !displayData) return;
    const map = {
      ytdBudget: ytdBudgetSectionRef,
      closedSites: topSitesSectionRef,
      footprint: footprintSectionRef,
      spendOverview: topSitesSectionRef,
    };
    const r = map[focusSection];
    if (!r) return;
    const id = window.setTimeout(() => {
      r.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => window.clearTimeout(id);
  }, [
    focusSection,
    displayData,
    currentData,
    selectedYear,
    selectedMonth,
    ytdBudgetOnly,
    ytdData,
    footprintOnly,
    closedSitesFinancialsOnly,
    spendOverviewOnly,
    mrcHistoryOnly,
    disconnectsOnly,
  ]);

  const handleMonthYearChange = (year, month) => {
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  const insightParagraph = useMemo(() => {
    if (!ytdData) return null;
    const monthName = MONTH_NAMES[reportMonth - 1] || "";
    const varianceStr = ytdData.current_ytd_variance || "";
    const runRate = ytdData.current_run_rate || "—";
    const totalYtd = ytdData.total_ytd_spend || "—";
    const budget = ytdData.annual_budget || "—";
    return (
      <>
        Through <strong>{monthName}</strong>, global billed spend is <strong className="text-blue-700">{totalYtd}</strong> against
        an annual plan of <strong>{budget}</strong>. Variance to the linear budget baseline is{" "}
        <strong
          className={
            parseVarianceSign(varianceStr) === "over"
              ? "text-orange-600"
              : parseVarianceSign(varianceStr) === "under"
              ? "text-emerald-700"
              : "text-gray-900"
          }
        >
          {varianceStr.replace(/\s*\([^)]*\)\s*$/, "").trim()}
        </strong>
        . At the current run rate of <strong className="text-emerald-700">{runRate}</strong> per month, review cumulative
        lines vs. budget to align spend with fiscal targets.
      </>
    );
  }, [ytdData, reportMonth]);

  const aiSpendProjectionBlock = useMemo(() => {
    if (!ytdData) return null;
  
    const parse = (v) => {
      if (!v) return null;
      const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
      return Number.isFinite(n) ? n : null;
    };
  
    const format = (v) =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(v);
  
    const monthNames = [
      "January","February","March","April","May","June",
      "July","August","September","October","November","December"
    ];
  
    const closedMonths = ytdData.closed_months;
    const reportMonthName = monthNames[closedMonths - 1] || "";
  
    const annualBudget = parse(ytdData.annual_budget);
    const ytdActual = parse(ytdData.ytd_actual);
    const ytdBudget = parse(ytdData.ytd_budget);
    const forecastEOY = parse(ytdData.forecast_eoy_spend);
    const requiredMonthly = parse(ytdData.required_monthly_spend);
    const currentAvg = parse(ytdData.current_avg_monthly_spend);

    const glFilter = selectedGlCode != null ? String(selectedGlCode).trim() : "";
    const glMeta = glFilter
      ? (Array.isArray(glCodes) ? glCodes : []).find(
          (g) =>
            String(g?.gl_code || "")
              .trim()
              .toLowerCase() === glFilter.toLowerCase(),
        )
      : null;
    const glLabel = glFilter
      ? formatGlCodeLabel(glFilter, glMeta?.description) || glFilter
      : "";
  
    if (
      annualBudget == null ||
      ytdActual == null ||
      ytdBudget == null
    ) {
      return <span>Insufficient data to generate summary.</span>;
    }
  
    const variance = ytdActual - ytdBudget;
    const varianceLabel =
      variance >= 0
        ? `${format(Math.abs(variance))} over YTD budget`
        : `${format(Math.abs(variance))} under YTD budget`;
  
    const paceGap =
      requiredMonthly != null && currentAvg != null
        ? currentAvg - requiredMonthly
        : null;
  
    const paceLabel =
      paceGap == null
        ? ""
        : paceGap > 0
        ? `spend pace needs to improve by ${format(Math.abs(paceGap))} per month`
        : `spend pace is ${format(Math.abs(paceGap))} below required`;
  
    return (
      <div className="text-sm text-gray-700 leading-relaxed">
  <p>
    Actual spend
    {glLabel ? (
      <>
        {" "}for GL code <strong>{glLabel}</strong>
      </>
    ) : null}{" "}
    through <strong>{reportMonthName}</strong> is{" "}
    <strong>{format(ytdActual)}</strong>.{" "}
    
    That is <strong>{varianceLabel}</strong>.{" "}
    
    {forecastEOY != null && (
      <>
        If current pace continues, year-end spend would be{" "}
        <strong>{format(forecastEOY)}</strong>.{" "}
      </>
    )}
    
    {requiredMonthly != null && (
      <>
        To still make the <strong>{format(annualBudget)}</strong> annual budget
        {glLabel ? (
          <>
            {" "}for GL code <strong>{glLabel}</strong>
          </>
        ) : null}
        , average monthly spend for the rest of the year must be{" "}
        <strong>{format(requiredMonthly)}</strong>.{" "}
      </>
    )}
    
    {currentAvg != null && requiredMonthly != null && (
      <>
        Current average is <strong>{format(currentAvg)}</strong>, so{" "}
        <strong>{paceLabel}</strong>.
      </>
    )}
  </p>
</div>
    );
  }, [ytdData, selectedGlCode, glCodes]);

  const varianceTrackingSubtext = useMemo(() => {
    if (!ytdData) return "";
    const annualNum = parseMoneyString(ytdData.annual_budget);
    const totalYtd = parseMoneyString(ytdData.total_ytd_spend);
    const completedMonths = Math.max(0, reportMonth - 1);
    if (annualNum == null || completedMonths <= 0 || totalYtd == null) return "Vs. linear baseline";
    const baseline = (annualNum / 12) * completedMonths;
    if (baseline <= 0) return "Vs. linear baseline";
    const pct = ((totalYtd - baseline) / baseline) * 100;
    const rounded = Math.round(pct * 10) / 10;
    const sign = rounded > 0 ? "+" : "";
    return `Tracking ${sign}${rounded}% ${rounded >= 0 ? "above" : "below"} baseline`;
  }, [ytdData, reportMonth]);

  if (wirelessUsageOnly) {
    return (
      <div className="report20-page pt-[155px] min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Wireless Usage</h1>
          <p className="text-sm text-slate-500 mt-2">Coming soon</p>
        </div>
      </div>
    );
  }

  if (ytdBudgetOnly) {
    if (annualBudget != null && ytdLoading && !ytdData) {
      return (
        <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)] flex items-center justify-center">
          <SpinnerCustom />
        </div>
      );
    }
  } else if (
    loading &&
    !footprintOnly &&
    !closedSitesFinancialsOnly &&
    !(spendOverviewOnly && currentData) &&
    !displayData
  ) {
    return (
      <div className="report20-page pt-[155px] min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <SpinnerCustom />
      </div>
    );
  }

  if (closedSitesFinancialsOnly) {
    if (closedSiteFinancialsLoading && !closedSiteFinancialsData) {
      return (
        <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)] flex items-center justify-center">
          <SpinnerCustom />
        </div>
      );
    }
    if (closedSiteFinancialsError && !closedSiteFinancialsData) {
      return (
        <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)] flex items-center justify-center p-4">
          <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white p-8 max-w-md text-center">
            <p className="text-sm font-medium text-[var(--crit,#c0262b)] mb-3">{closedSiteFinancialsError.message || "Failed to load report data"}</p>
            <button
              type="button"
              onClick={() => refreshClosedSiteFinancials && refreshClosedSiteFinancials()}
              className="inline-flex items-center justify-center rounded-md bg-[var(--accent,#a51c1c)] px-4 py-2 text-[13px] font-medium text-white hover:opacity-90"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    if (!closedSiteFinancialsLoading && !closedSiteFinancialsData) {
      return (
        <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)] flex items-center justify-center p-4">
          <p className="text-[13px] text-[var(--muted,#6b6a66)]">Select a company and month to load closed-site financials.</p>
        </div>
      );
    }
  }

  if (footprintOnly) {
    if (siteFootprintLoading && !siteFootprintData) {
      return (
        <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)] flex items-center justify-center">
          <SpinnerCustom />
        </div>
      );
    }
    if (siteFootprintError && !siteFootprintData) {
      return (
        <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)] flex items-center justify-center p-4">
          <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white p-8 max-w-md text-center">
            <p className="text-sm font-medium text-[var(--crit,#c0262b)] mb-3">
              {siteFootprintError.message || "Failed to load site footprint report."}
            </p>
            <button
              type="button"
              onClick={() => refreshSiteFootprint()}
              className="inline-flex items-center justify-center rounded-md bg-[var(--accent,#a51c1c)] px-4 py-2 text-[13px] font-medium text-white hover:opacity-90"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
  }

  if (spendOverviewOnly) {
    if (siteAnalyticsLoading && !currentData) {
      return (
        <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)] flex items-center justify-center">
          <SpinnerCustom />
        </div>
      );
    }
    if (siteAnalyticsError && !currentData) {
      return (
        <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8 max-w-md text-center">
            <p className="text-sm font-medium text-red-600 mb-3">
              {siteAnalyticsError.message || "Failed to load spend overview"}
            </p>
            <button
              type="button"
              onClick={() => refreshSiteAnalytics && refreshSiteAnalytics()}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    if (!currentData) {
      return (
        <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)] flex items-center justify-center p-4">
          <p className="text-sm text-gray-500">No inventory data available</p>
        </div>
      );
    }

    const invTopSites = currentData.topSites || [];
    const invAllSites = currentData.allSites || [];
    const invProviderSpending = currentData.providerSpending || [];
    const invCategorySpending = currentData.categorySpending || [];
    const invStaleLoading = Boolean(currentData) && siteAnalyticsLoading;
    const invTotalMrc = Number(currentData.totalSpent) || 0;
    // Location = address groups; Sites = configured site details (same as Detailed Inventory)
    const invLocationCount =
      currentData.totalLocations ?? currentData.totalSites ?? invAllSites.length;
    const invSiteCount =
      currentData.siteCount != null
        ? currentData.siteCount
        : invAllSites.filter((g) => {
            if (g.needs_master) return false;
            return (
              String(g.site_name || "").trim() !== "" ||
              (g.site_status != null && String(g.site_status).trim() !== "")
            );
          }).length;
    const invProviderCount = invProviderSpending.length;
    const invBasisLabel = `Inventory MRC · ${MONTH_NAMES[currentMonth - 1] || ""} ${currentYear}`.trim();

    const exportSpendOverview = () => {
      const escapeCsv = (value) => {
        if (value == null) return "";
        const str = String(value);
        const escaped = str.replace(/"/g, '""');
        return /[",\n]/.test(str) ? `"${escaped}"` : escaped;
      };
      const lines = [
        "Top 10 spending sites",
        ["Rank", "Site", "Services", "Total MRC"].map(escapeCsv).join(","),
        ...[...(invAllSites.length ? invAllSites : invTopSites)]
          .sort((a, b) => (parseFloat(b.totalMRC) || 0) - (parseFloat(a.totalMRC) || 0))
          .slice(0, 10)
          .map((site, i) =>
            [i + 1, site.site, site.recordCount ?? "", site.totalMRC].map(escapeCsv).join(",")
          ),
        "",
        "Spend by provider",
        ["Provider", "Total MRC", "Share %"].map(escapeCsv).join(","),
        ...invProviderSpending.map((p) =>
          [p.provider, p.totalMRC, p.percentage].map(escapeCsv).join(",")
        ),
        "",
        "Spend by service category",
        ["Category", "Total MRC", "Share %"].map(escapeCsv).join(","),
        ...invCategorySpending.map((c) =>
          [c.category, c.totalMRC, c.percentage].map(escapeCsv).join(",")
        ),
      ];
      const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "spend-overview.csv";
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)]">
        <div className="w-full px-5 py-2 pb-14">
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-5">
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
                Reports
              </p>
              <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
                Spend Overview
              </h1>
              <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)]">
                Top locations, provider mix and service categories from current inventory
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-2.5">
              <div className="inline-flex items-stretch overflow-hidden rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white shadow-sm">
                <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    Basis
                  </label>
                  <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                    {invBasisLabel}
                  </div>
                </div>
                <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    Total MRC
                  </label>
                  <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                    {formatUsd0(invTotalMrc)}
                  </div>
                </div>
                <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    Location
                  </label>
                  <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                    {Number(invLocationCount || 0).toLocaleString()}
                  </div>
                </div>
                <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    Sites
                  </label>
                  <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                    {Number(invSiteCount || 0).toLocaleString()}
                  </div>
                </div>
                <div className="px-3 py-2">
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    Providers
                  </label>
                  <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                    {invProviderCount}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={exportSpendOverview}
                className="inline-flex h-[52px] items-center gap-2 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] shadow-sm hover:bg-[var(--sunken,#eeece7)]"
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Export
              </button>
            </div>
          </div>

          <div className="relative min-h-[min(60vh,400px)] mb-6">
            <Report20DataLoadingOverlay show={invStaleLoading} message="Refreshing inventory…" />
            <div className={invStaleLoading ? dimStaleWhileLoading : ""}>
              <div className="flex flex-col gap-4">
                <div
                  ref={topSitesSectionRef}
                  id="report20-section-spend-overview"
                  className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden"
                >
                  <Report20TopSitesTable
                    topSites={invTopSites}
                    allSites={invAllSites}
                    snapshotData={null}
                    totalSpent={invTotalMrc}
                  />
                </div>

                <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
                  <Report20ProviderDonutChart data={invProviderSpending} />
                </div>

                <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
                  <Report20CategoryBarChart data={invCategorySpending} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mrcHistoryOnly) {
    return (
      <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)]">
        <div className="w-full px-5 py-2 pb-14">
          <Report20MrcHistoryView
            tenantId={tenantId}
            year={reportYear}
            onYearChange={setSelectedYear}
          />
        </div>
      </div>
    );
  }

  if (disconnectsOnly) {
    return (
      <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)]">
        <div className="w-full px-5 py-2 pb-14">
          <Report20DisconnectsView tenantId={tenantId} />
        </div>
      </div>
    );
  }

  if (footprintOnly) {
    return (
      <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)]">
        <div className="w-full px-5 py-2 pb-14">
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-5">
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
                Reports
              </p>
              <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
                Site Footprint
              </h1>
              <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)]">
                Live footprint based on current active inventory and site status.
              </p>
            </div>
          </div>
          {siteFootprintError && (
            <div className="mb-4 rounded-[10px] border border-[var(--crit-soft,#fbe6e6)] bg-[var(--crit-soft,#fbe6e6)] px-4 py-3 text-[13px] text-[var(--crit,#c0262b)]">
              {siteFootprintError.message || "Failed to load site footprint report."}{" "}
              <button type="button" className="underline font-medium" onClick={() => refreshSiteFootprint()}>
                Retry
              </button>
            </div>
          )}
          <div
            ref={footprintSectionRef}
            id="report20-section-footprint"
            className="relative min-h-[min(60vh,480px)] scroll-mt-28"
          >
            <Report20DataLoadingOverlay
              show={siteFootprintLoading && Boolean(siteFootprintData)}
              message="Loading footprint report…"
            />
            <div className={siteFootprintLoading && siteFootprintData ? dimStaleWhileLoading : ""}>
              {!siteFootprintError && siteFootprintData && (
                <Report20SiteFootprintChangesView data={siteFootprintData} />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!ytdBudgetOnly && error && !footprintOnly && !closedSitesFinancialsOnly && !displayData) {
    return (
      <div className="report20-page pt-[155px] min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8 max-w-md text-center">
          <p className="text-sm font-medium text-red-600 mb-3">{error.message || "Failed to load report data"}</p>
          <button
            type="button"
            onClick={() => refreshInvoiceReport && refreshInvoiceReport()}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!ytdBudgetOnly && !footprintOnly && !closedSitesFinancialsOnly && !displayData) {
    return (
      <div className="report20-page pt-[155px] min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <p className="text-sm text-gray-500">No report data available</p>
      </div>
    );
  }

  const varianceBadge =
    parseVarianceSign(ytdData?.current_ytd_variance) === "over"
      ? { label: "OVER", className: ytdBudgetOnly ? "bg-rose-100 text-rose-800" : "bg-orange-100 text-orange-800" }
      : parseVarianceSign(ytdData?.current_ytd_variance) === "under"
      ? { label: "UNDER", className: "bg-emerald-100 text-emerald-800" }
      : { label: "ON TRACK", className: "bg-gray-100 text-gray-700" };

  if (closedSitesFinancialsOnly && closedSiteFinancialsData) {
    return (
      <div className="report20-page pt-[80px] min-h-screen bg-[var(--ground,#f5f4f1)]">
        <div className="w-full px-5 py-2 pb-14">
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-5">
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
                Reports
              </p>
              <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
                Closed Site Financials
              </h1>
              <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)]">
                Cost optimization for closed sites based on current inventory data. Only sites marked Closed.
              </p>
            </div>
          </div>
          <Report20ClosedSiteFinancialsView
            report={closedSiteFinancialsData}
            isLoading={closedSiteFinancialsLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`report20-page min-h-screen ${
      ytdBudgetOnly ? "pt-[80px] bg-[var(--ground,#f5f4f1)]" : "pt-[155px] bg-gradient-to-b from-slate-50 via-white to-slate-50/80"
    }`}>
      <div className={ytdBudgetOnly ? "w-full px-5 py-2 pb-14" : "max-w-full lg:max-w-[85%] mx-4 sm:mx-5 lg:mx-auto mt-8 pb-14"}>
        {/* <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              {ytdBudgetOnly ? "YTD Budget Tracking" : footprintOnly ? "Site Footprint Changes" : "Report 2.0"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {ytdBudgetOnly
                ? "Invoice-based run rate, variance, cumulative trend, and monthly ledger"
                : footprintOnly
                ? "Count-based site openings, closings, net delta, active sites, and baseline targets"
                : "Portfolio insight, YTD chart, and spend breakdown"}
            </p>
          </div>
          <MonthYearSelector
            onChange={handleMonthYearChange}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            showCurrentOption={true}
          />
        </div> */}

<div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-5">
  <div>
    {ytdBudgetOnly ? (
      <>
        {/* <p className="text-[12px] text-[var(--muted,#6b6a66)]">
          Reports
          <span className="mx-1.5 text-[var(--faint,#9a9a95)]">/</span>
          <span className="font-medium text-[var(--ink,#1c1c1a)]">YTD Budget Tracking</span>
        </p> */}
        <p className="mt-3 text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
          Reports
        </p>
        <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
          YTD Budget Tracking
        </h1>
        <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)]">
          Invoice based run rate, variance, cumulative trend and the monthly ledger
        </p>
      </>
    ) : (
      <>
    <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
      {footprintOnly ? "Site Footprint" : "Report 2.0"}
    </h1>
    <p className="text-sm text-slate-500 mt-1">
      {footprintOnly
        ? "Live footprint based on current active inventory and site status."
        : "Spend Breakdown"}
    </p>
      </>
    )}
  </div>

  {!footprintOnly && (
    <>
    {/* <MonthYearSelector
      onChange={handleMonthYearChange}
      selectedYear={selectedYear}
      selectedMonth={selectedMonth}
      showCurrentOption={true}
    /> */}
{ytdBudgetOnly ? (
<div className="flex flex-wrap items-end gap-2.5">
  <div className="inline-flex items-stretch overflow-hidden rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white shadow-sm">
    <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
      <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
        Fiscal year
      </label>
      <CustomSelect
        value={selectedYear ?? ""}
        onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
        className="mt-0.5 h-7 bg-transparent pr-6 text-[13px] font-medium text-[var(--ink,#1c1c1a)] focus:outline-none"
      >
        {yearOptions.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </CustomSelect>
    </div>
    <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
      <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
        GL code
      </label>
      <CustomSelect
        value={selectedGlCode}
        onChange={(e) => setSelectedGlCode(e.target.value)}
        className="mt-0.5 h-7 min-w-[10rem] bg-transparent pr-6 text-[13px] font-medium text-[var(--ink,#1c1c1a)] focus:outline-none"
      >
        <option value="">All GL codes</option>
        {glCodeOptions.map((g) => (
          <option key={g.gl_code} value={g.gl_code}>
            {formatGlCodeLabel(g.gl_code, g.description)}
          </option>
        ))}
      </CustomSelect>
    </div>
    <div className="px-3 py-2">
      <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
        Closed through
      </label>
      <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
        {formatClosedThrough(ytdData?.last_closed_month, ytdData?.closed_months, reportYear)}
      </div>
    </div>
  </div>
  <button
    type="button"
    onClick={() => ytdExportRef.current?.()}
    className="inline-flex h-[52px] items-center gap-2 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] shadow-sm hover:bg-[var(--sunken,#eeece7)]"
  >
    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
    Export
  </button>
</div>
) : (
<div className="flex flex-wrap items-end gap-3">
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
      Fiscal year
    </label>
    <CustomSelect
      value={selectedYear ?? ""}
      onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
      className={ytdControlClass}
    >
      {yearOptions.map((y) => (
        <option key={y} value={y}>
          {y}
        </option>
      ))}
    </CustomSelect>
  </div>
</div>
)}

    </>
  )}
</div>

        {!footprintOnly && (
        <div
          ref={ytdBudgetSectionRef}
          id="report20-section-ytd-budget"
          className="scroll-mt-28"
        >
        {annualBudget == null && (
          <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {showReportBudgetCard ? (
              <>
                Set an annual budget for {reportYear} using <strong>Report Budget</strong> above or on the <strong>Reports</strong>{" "}
                tab to load YTD metrics and the chart below.
              </>
            ) : (
              <>
                Set an annual budget for {reportYear} on the <strong>Reports</strong> tab (Report Budget) to load YTD metrics and the
                chart below.
              </>
            )}
          </div>
        )}

        {/* <div className="relative min-h-[min(50vh,420px)]"> */}
        <div className={`relative ${!ytdBudgetOnly ? "hidden" : "min-h-[min(50vh,420px)]"}`}>
          <Report20DataLoadingOverlay show={ytdStaleLoading} message="Loading YTD dataâ€¦" />
          <div className={ytdStaleLoading ? dimStaleWhileLoading : ""}>
        {ytdBudgetOnly ? (
          <>
            <div className="mb-5 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Star className="h-4 w-4 text-[var(--accent,#a51c1c)]" aria-hidden />
                  <h2 className="text-[15px] font-semibold text-[var(--ink,#1c1c1a)]">Spend projection</h2>
                  <span className="inline-flex rounded-full bg-[var(--crit-soft,#fbe6e6)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--accent,#a51c1c)]">
                    Forecast
                  </span>
                </div>
                <p className="text-[11px] text-[var(--muted,#6b6a66)]">
                  Written automatically from the report figures. Updates when a month closes.
                </p>
              </div>
              {aiSpendProjectionBlock && ytdData ? (
                <div className="text-[13px] leading-relaxed text-[var(--ink,#1c1c1a)] border-l-2 border-[var(--accent,#a51c1c)] pl-3">
                  {aiSpendProjectionBlock}
                </div>
              ) : ytdLoading && annualBudget != null ? (
                <div className="flex justify-center py-4">
                  <SpinnerCustom />
                </div>
              ) : ytdError && annualBudget != null ? (
                <p className="text-sm text-red-600">
                  {ytdError.message || "Could not load YTD data."}{" "}
                  <button type="button" className="underline font-medium" onClick={() => refreshYtd()}>
                    Retry
                  </button>
                </p>
              ) : (
                <p className="text-sm text-[var(--muted,#6b6a66)]">Configure annual budget to see the forecast narrative.</p>
              )}
            </div>

            {ytdData && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
              <YtdKpiCard
                label="Annual budget"
                value={ytdData.annual_budget}
                badge={`FY${String(reportYear).slice(-2)}`}
                hint={
                  parseMoneyString(ytdData.annual_budget) != null
                    ? `12 months at ${formatUsd0(parseMoneyString(ytdData.annual_budget) / 12)}`
                    : null
                }
              />
              <YtdKpiCard
                label="YTD actual"
                value={ytdData.ytd_actual}
                badge="Actual"
                hint={
                  ytdData.closed_months != null
                    ? `${ytdData.closed_months} closed month${Number(ytdData.closed_months) === 1 ? "" : "s"}`
                    : null
                }
                tooltip="YTD Actual = Sum of Actual for Closed Months"
              />
              <YtdKpiCard
                label="Forecast EOY spend"
                value={ytdData.forecast_eoy_spend || "â€”"}
                badge="Forecast"
                tooltip="Forecast EOY Spend = (YTD Actual / Closed Months) Ã— 12"
              />
              <YtdKpiCard
                label="YTD variance"
                value={ytdData.ytd_variance?.replace(/\s*\([^)]*\)\s*$/, "").trim() || "â€”"}
                valueClassName={
                  parseVarianceSign(ytdData.ytd_variance) === "over"
                    ? "text-[var(--crit,#c0262b)]"
                    : parseVarianceSign(ytdData.ytd_variance) === "under"
                    ? "text-[var(--good,#1f6b34)]"
                    : "text-[var(--ink,#1c1c1a)]"
                }
                hint={
                  parseVarianceSign(ytdData.ytd_variance) === "over"
                    ? "Over budget to date"
                    : parseVarianceSign(ytdData.ytd_variance) === "under"
                    ? "Under budget to date"
                    : null
                }
                tooltip="YTD Variance = YTD Actual − YTD Budget"
              />
              <YtdKpiCard
                label="Remaining budget"
                value={ytdData.remaining_budget}
                hint={
                  ytdData.closed_months != null
                    ? `For ${Math.max(0, 12 - Number(ytdData.closed_months))} months`
                    : null
                }
                tooltip="Remaining Budget = Annual Budget − YTD Actual"
              />
              <YtdKpiCard
                label="Required monthly spend"
                value={ytdData.required_monthly_spend || "â€”"}
                hint="To land on budget"
                tooltip="Required Monthly Spend = Remaining Budget / Remaining Months"
              />
              <YtdKpiCard
                label="Stabilized monthly run rate"
                value={ytdData.current_avg_monthly_spend || "â€”"}
                tooltip="Current Avg Monthly Spend = YTD Actual / Closed Months"
              />
              <YtdKpiCard
                label="Pace gap"
                value={ytdData.pace_gap?.replace(/\s*\([^)]*\)\s*$/, "").trim() || "â€”"}
                valueClassName={
                  parseVarianceSign(ytdData.pace_gap) === "over"
                    ? "text-[var(--crit,#c0262b)]"
                    : parseVarianceSign(ytdData.pace_gap) === "under"
                    ? "text-[var(--good,#1f6b34)]"
                    : "text-[var(--ink,#1c1c1a)]"
                }
                hint={
                  parseVarianceSign(ytdData.pace_gap) === "over"
                    ? "Run rate is above what is required"
                    : parseVarianceSign(ytdData.pace_gap) === "under"
                    ? "Run rate is below what is required"
                    : null
                }
                tooltip="Pace Gap = Current Avg Monthly Spend − Required Monthly Spend"
              />
            </div>
            )}
            
            {ytdData?.chart_value && (
              <div className="mb-5 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
                <div className="px-5 pt-4 pb-1">
                  <h3 className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)]">Trend</h3>
                  <p className="mt-1 text-[12px] text-[var(--muted,#6b6a66)]">
                    Bars show cumulative budget. The solid line shows actual spend; the dashed line projects year end spend at the current pace. Above the bars means over budget.
                  </p>
                </div>
                <div className="px-3 sm:px-5 pb-4 pt-2">
                  <Report20YtdBudgetTrendChart
                    chartValue={ytdData.chart_value}
                    closedMonthsCount={ytdData.closed_months}
                    annualBudget={ytdData.annual_budget}
                    forecastEoySpend={ytdData.forecast_eoy_spend}
                  />
                </div>
              </div>
            )}

            {ytdData?.chart_value && (
              <div className="mb-6">
                <Report20MonthlyFinancialLedger
                  ledger={ytdData.monthly_financial_ledger}
                  year={reportYear}
                  onRefresh={refreshYtd}
                  monthSummaryEnabled={Boolean(ytdData.month_summary_enabled)}
                  canEdit={canEditReportBudget}
                  exportRef={ytdExportRef}
                />
              </div>
            )}

            <Report20SpendByGlCard
              rows={ytdData?.spend_by_gl}
              lastClosedMonth={ytdData?.last_closed_month}
              selectedGlCode={selectedGlCode}
            />

{ytdData?.provider_table && showProviderBudgetVsSpend && (
  <div className="mb-8">
    <Report20ProviderTable
      data={ytdData.provider_table}
      closedMonthsCount={ytdData.closed_months}
    />
  </div>
)}
          </>
        ) : null }
          </div>
        </div>
        </div>
        )}

        {/* <div
          className={`rounded-xl border border-blue-100 bg-blue-50/80 px-4 py-3 ${
            ytdBudgetOnly || footprintOnly ? "mb-6" : "mb-4"
          }`}
        >
          <p className="text-sm text-blue-900">
            Data context: {MONTH_NAMES[reportMonth - 1]} {reportYear}
            {isReportCurrentMonth && (
              <span className="font-medium"> — current month data may still be incomplete.</span>
            )}
          </p>
        </div> */}
        {/* {!footprintOnly && !closedSitesFinancialsOnly && (
  <div
    className={`rounded-xl border border-blue-100 bg-blue-50/80 px-4 py-3 ${
      ytdBudgetOnly ? "mb-6" : "mb-4"
    }`}
  >
    <p className="text-sm text-blue-900">
      Data context: {MONTH_NAMES[reportMonth - 1]} {reportYear}
      {isReportCurrentMonth && (
        <span className="font-medium"> — current month dataaaa may still be incomplete.</span>
      )}
    </p>
  </div>
)} */}
      </div>
    </div>
  );
};

export default Report20Page;